import { Auth } from '@util/Auth';
import { MenoUserData } from 'hydra-data-types';
import React, { ReactElement } from 'react';
import { isEmpty } from 'ts-utils';
import { LayoutScreenConfig, ScreenType, SharedScreenName } from '../../config/FlowTypes';
import { AppSelector, useAppStore } from '../../store/Store';
import { ScreenWrapper } from '../styles/Containers';
import { SummaryCard, SummaryData } from '../styles/SummaryCard';
import { NavigationProps } from './FlowNavigation';
import { PresentationLayout } from './layouts/PresentationLayout';

// Because we're not exposing the PresentationLayoutConfig directly we have a separate layout config
type UserInfoRecapLayoutConfig = {
  title: string[];
  body: string[];
  primaryButtonTitle: string;
};

export type UserInfoRecapScreenConfig<ScreenNames extends string> = LayoutScreenConfig<
  ScreenType.UserInfoRecap,
  UserInfoRecapLayoutConfig,
  ScreenNames
>;

type UserInfoRecapScreenProps<ScreenNames extends string> = NavigationProps<
  UserInfoRecapScreenConfig<ScreenNames>,
  ScreenNames
>;

export const UserInfoRecapScreen = <ScreenNames extends string>(
  props: UserInfoRecapScreenProps<ScreenNames>
): ReactElement => {
  const { layout, navigation } = props;
  const { userData, user } = useAppStore((state) => ({
    userData: AppSelector.userData(state),
    user: AppSelector.user(state),
  }));

  function onSubmit() {
    if (Auth.isUserSignedIn() && user?.userId !== undefined) {
      // TODO: Unsafe cast. ScreenNames should really have to extend SharedScreenName.
      navigation.goTo(SharedScreenName.LoadingResults as ScreenNames);
    } else {
      navigation.next(props);
    }
  }

  return (
    <ScreenWrapper>
      <PresentationLayout
        title={layout.title[0]}
        content={[
          ...layout.body.map((content) => ({ type: 'text' as const, content })),
          {
            type: 'custom',
            content: (
              <SummaryCard
                heading="Your Assessment"
                data={menoSummaryData(userData as MenoUserData)}
              />
            ),
          },
        ]}
        primaryButtonTitle={layout.primaryButtonTitle}
        onPressPrimaryButton={onSubmit}
      />
    </ScreenWrapper>
  );
};

function menoSummaryData(menoUserData: MenoUserData): SummaryData {
  const hotFlashIntensity = menoUserData.hotFlashIntensity?.[0];
  const hotFlashIntensityStr = `, ${hotFlashIntensity}`;
  const hotFlashes = `${menoUserData.hotFlash} per day${!isEmpty(hotFlashIntensity) ? hotFlashIntensityStr : ''}`;

  const sleepQuality = menoUserData.sleepQuality?.[0];
  const sleepQualityStr = `, ${menoUserData.sleepQuality} sleep quality`;
  const nightSweats =
    `${menoUserData.nightSweatsHowMany ?? '0'} per night` +
    (!isEmpty(sleepQuality) ? sleepQualityStr : '');

  return [
    {
      items: [
        {
          heading: 'Hot flushes',
          items: menoUserData.hotFlash ? [hotFlashes.toLowerCase()] : [],
        },
        { heading: 'Night sweats', items: [nightSweats.toLowerCase()] },
        { heading: 'Anxiety or depression', items: menoUserData.anxiety ?? [] },
        { heading: 'Well rested', items: menoUserData.wellRested ?? [] },
      ].filter(({ items }) => items.length > 0),
    },
  ];
}
